import React, { useEffect, useState } from "react";
import AndroidIcon from "@mui/icons-material/Android";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import ScreenshotLangPicker from "../../components/ScreenshotLangPicker";
import { appendScreenshotLang, useScreenshotTranslation } from "../../lib/screenshotI18n";
import {
    advertisementScreenshotGalleryPath,
    advertisementGoogleAdPath,
    advertisementFacebookAdPath,
    advertisementAmazonTabletPath,
    COMPANY_LOGO_PATH,
    COMPANY_GOOGLE_HEADER_PATH,
    COMPANY_GOOGLE_ADMOB_PATH,
    COMPANY_FACEBOOK_COVER_PATH,
    COMPANY_YOUTUBE_COVER_PATH,
    JIGSAW_GOOGLE_ADMOB_PATH,
    WORD_WHEEL_GOOGLE_ADMOB_PATH,
    SLIDING_GOOGLE_ADMOB_PATH,
    webGoogleAdPath,
} from "../advertisement/advertisementRoutes";
import { WEB_AD_FRAME, webAdShotsByGroup } from "../../pages/advertisement/webAdShots";
import {
    JIGSAW_MOBILE_TITLE,
    screenshotPath as jigsawScreenshotPath,
} from "../../prototype/mobile-jigsaw/routes";
import { JIGSAW_SCREENSHOT_SCENES } from "../../prototype/mobile-jigsaw/screenshotScenes";
import { formatScreenshotAccessValidThroughLabel } from "../../prototype/mobile-jigsaw/lib/screenshotAccess";
import { formatScreenshotAccessValidThroughLabel as formatWordWheelScreenshotAccessValidThroughLabel } from "../../prototype/mobile-word-wheel/lib/screenshotAccess";
import { animationPath as slidingAnimationPath } from "../../prototype/mobile-sliding/animation/routes";
import { SLIDING_ANIMATION_SCENES } from "../../prototype/mobile-sliding/animation/animationScene";
import {
    SLIDING_PUZZLE_QUEST_TITLE,
    screenshotPath as slidingScreenshotPath,
} from "../../prototype/mobile-sliding/routes";
import {
    isScreenshotSceneConfigured,
    SLIDING_SCREENSHOT_SCENES,
} from "../../prototype/mobile-sliding/screenshotScenes";
import { openBrowserPopup, openCapturePagePopup, openScreenshotGalleryPopup } from "./openBrowserPopup";
import {
    APP_SITE_ID,
    APP_SITE_REGISTRY,
    resolvePublicAppSupportUrl,
    resolvePublicLegalUrl,
    resolvePublicMarketingUrl,
    resolvePublicSiteOrigin,
} from "../../config/appSiteLinks";
import {
    resolvePublicDataDeletionLinksForSiteApp,
    resolvePublicDataDeletionRequestUrl,
} from "../../pages/login/data-deletion/dataDeletionRequestRoutes";
import {
    JIGSAW_MUSE_GOOGLE_PLAY_URL,
    SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL,
    WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
} from "../../config/nativeAppsVisibility";
import {
    WORD_WHEEL_QUEST_TITLE,
    screenshotPath as wordWheelScreenshotPath,
} from "../../prototype/mobile-word-wheel/routes";
import {
    isScreenshotSceneConfigured as isWordWheelScreenshotSceneConfigured,
    WORD_WHEEL_SCREENSHOT_SCENES,
} from "../../prototype/mobile-word-wheel/screenshotScenes";

const WORD_SEARCH_MINI_TITLE = "Word Search Mini";

const GOOGLE_PLAY_URL_BY_APP = {
    [APP_SITE_ID.JIGSAW_MUSE]: JIGSAW_MUSE_GOOGLE_PLAY_URL,
    [APP_SITE_ID.SLIDING_PUZZLE_QUEST]: SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL,
    [APP_SITE_ID.WORD_WHEEL_QUEST]: WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
};

const MOBILE_APP_PUBLIC_LINKS = [
    { appId: APP_SITE_ID.JIGSAW_MUSE, label: JIGSAW_MOBILE_TITLE },
    { appId: APP_SITE_ID.SLIDING_PUZZLE_QUEST, label: SLIDING_PUZZLE_QUEST_TITLE },
    { appId: APP_SITE_ID.WORD_WHEEL_QUEST, label: WORD_WHEEL_QUEST_TITLE },
    {
        appId: APP_SITE_ID.WORD_SEARCH_MINI,
        label: WORD_SEARCH_MINI_TITLE,
        note: "Mini word search app",
    },
];

const PUBLIC_LINK_PRODUCTS = [
    {
        appId: APP_SITE_ID.WEB,
        label: APP_SITE_REGISTRY[APP_SITE_ID.WEB].displayName,
        note: "Crossword, word search, hidden message, and other web puzzles",
    },
    ...MOBILE_APP_PUBLIC_LINKS,
];

const PUBLIC_LINK_FIELDS = [
    { label: "Web", resolve: () => resolvePublicSiteOrigin() },
    { label: "Support", resolve: (appId) => resolvePublicAppSupportUrl(appId) },
    { label: "Marketing", resolve: (appId) => resolvePublicMarketingUrl(appId) },
    { label: "Privacy", resolve: (appId) => resolvePublicLegalUrl(appId, "privacy") },
    { label: "Terms", resolve: (appId) => resolvePublicLegalUrl(appId, "terms") },
];

const PUBLIC_LINKS_TAB_STORAGE_KEY = "appScreenshotLinks.publicLinksTab";
const SELECTED_TAB_STORAGE_KEY = "appScreenshotLinks.selectedTab.v4";
const LEGACY_TAB_STORAGE_KEYS = [
    "appScreenshotLinks.selectedTab.v3",
    "appScreenshotLinks.selectedTab.v2",
    "appScreenshotLinks.selectedTab",
];
const TAB_COUNT = 7;
const WEB_PLAY_PAGES = [
    { label: "Jigsaw", path: "/puzzles/jigsaw" },
    { label: "Word Wheel", path: "/puzzles/word-wheel" },
    { label: "Crossword", path: "/puzzles/crossword" },
    { label: "Tutor", path: "/tutors" },
];
/** Former "Stripe" tab index in v2 (Company=0, Stripe=1, …). */
const REMOVED_STRIPE_TAB_INDEX = 1;
/** Former "Link coverage" tab index in v1. */
const REMOVED_LINK_COVERAGE_TAB_INDEX = 3;

function readSelectedTab() {
    if (typeof window === "undefined") return 0;
    try {
        const saved = window.localStorage.getItem(SELECTED_TAB_STORAGE_KEY);
        if (saved != null) {
            const savedTab = Number.parseInt(saved, 10);
            return Number.isInteger(savedTab) && savedTab >= 0 && savedTab < TAB_COUNT
                ? savedTab
                : 0;
        }
        for (const key of LEGACY_TAB_STORAGE_KEYS) {
            const legacy = window.localStorage.getItem(key);
            if (legacy == null) continue;
            let legacyTab = Number.parseInt(legacy, 10);
            if (!Number.isInteger(legacyTab) || legacyTab < 0) continue;
            if (key === "appScreenshotLinks.selectedTab") {
                // v1 → v2: drop link-coverage tab
                legacyTab =
                    legacyTab === REMOVED_LINK_COVERAGE_TAB_INDEX
                        ? 2
                        : legacyTab > REMOVED_LINK_COVERAGE_TAB_INDEX
                            ? legacyTab - 1
                            : legacyTab;
            }
            if (key === "appScreenshotLinks.selectedTab" || key === "appScreenshotLinks.selectedTab.v2") {
                // v2 → v3: drop Stripe tab
                legacyTab =
                    legacyTab === REMOVED_STRIPE_TAB_INDEX
                        ? 0
                        : legacyTab > REMOVED_STRIPE_TAB_INDEX
                            ? legacyTab - 1
                            : legacyTab;
            }
            if (key === "appScreenshotLinks.selectedTab.v3") {
                // v3 → v4: insert Word Search Mini before WEB (WEB was index 5)
                if (legacyTab >= 5) legacyTab += 1;
            }
            return legacyTab < TAB_COUNT ? legacyTab : 0;
        }
        return 0;
    } catch {
        return 0;
    }
}

function screenshotPageHref(path, lang) {
    if (typeof window === "undefined") return path;
    return `${window.location.origin}${appendScreenshotLang(path, lang)}`;
}

/** Sized popup with browser toolbar — for OBS / App Store capture pages. */
function openCapturePopup(path, windowName, lang) {
    if (typeof window === "undefined") return;
    openCapturePagePopup(screenshotPageHref(path, lang), windowName);
}

function openGalleryPopup(path, windowName, lang) {
    if (typeof window === "undefined") return;
    openScreenshotGalleryPopup(screenshotPageHref(path, lang), windowName);
}

function ExpiryNote({ formatLabel = formatScreenshotAccessValidThroughLabel }) {
    return (
        <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600, mb: 1.5 }}>
            Available through {formatLabel()}.
        </Typography>
    );
}

function LinkRow({ href, children, onClick, caption }) {
    return (
        <Box sx={{ py: 0.35 }}>
            <Link
                href={href}
                onClick={onClick}
                sx={{ cursor: "pointer", fontWeight: 500 }}
            >
                {children}
            </Link>
            {caption ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    {caption}
                </Typography>
            ) : null}
        </Box>
    );
}

function readPublicLinksTab() {
    if (typeof window === "undefined") return 0;
    try {
        const saved = Number.parseInt(
            window.localStorage.getItem(PUBLIC_LINKS_TAB_STORAGE_KEY),
            10,
        );
        return Number.isInteger(saved) && saved >= 0 && saved < PUBLIC_LINK_PRODUCTS.length
            ? saved
            : 0;
    } catch {
        return 0;
    }
}

function PublicProductLinks({ appId }) {
    const playUrl = GOOGLE_PLAY_URL_BY_APP[appId];
    return (
        <Stack spacing={0.35} sx={{ maxWidth: 720 }}>
            {PUBLIC_LINK_FIELDS.map(({ label: linkLabel, resolve }) => {
                const href = resolve(appId);
                return (
                    <LinkRow key={linkLabel} href={href} caption={href}>
                        {linkLabel}
                    </LinkRow>
                );
            })}
            {appId === APP_SITE_ID.WEB ? (
                <LinkRow
                    href={resolvePublicDataDeletionRequestUrl()}
                    caption={resolvePublicDataDeletionRequestUrl()}
                >
                    Data deletion
                </LinkRow>
            ) : null}
            {resolvePublicDataDeletionLinksForSiteApp(appId).map(
                ({ platform, label: deletionLabel, url }) => (
                    <LinkRow key={platform} href={url} caption={url}>
                        {deletionLabel}
                    </LinkRow>
                ),
            )}
            {playUrl ? (
                <LinkRow href={playUrl} caption={playUrl}>
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                        }}
                    >
                        <AndroidIcon sx={{ fontSize: 19, color: "#3BA666" }} />
                        Google Play
                    </Box>
                </LinkRow>
            ) : null}
        </Stack>
    );
}

function TabPanel({ value, index, children }) {
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`app-links-tabpanel-${index}`}
            aria-labelledby={`app-links-tab-${index}`}
            sx={{ p: { xs: 2, sm: 2.5 } }}
        >
            {value === index ? children : null}
        </Box>
    );
}

/**
 * Admin helper page — App Store + screenshot capture links (current host).
 */
export default function AppAndScreenshotLinksPage() {
    const { lang } = useScreenshotTranslation();
    const [activeTab, setActiveTab] = useState(readSelectedTab);
    const [publicLinksTab, setPublicLinksTab] = useState(readPublicLinksTab);

    useEffect(() => {
        try {
            window.localStorage.setItem(SELECTED_TAB_STORAGE_KEY, String(activeTab));
        } catch {
            // Local storage can be unavailable in private or restricted browser contexts.
        }
    }, [activeTab]);

    useEffect(() => {
        try {
            window.localStorage.setItem(PUBLIC_LINKS_TAB_STORAGE_KEY, String(publicLinksTab));
        } catch {
            // Local storage can be unavailable in private or restricted browser contexts.
        }
    }, [publicLinksTab]);

    const localizedHref = (path) => screenshotPageHref(path, lang);

    const openScreenshotLink = (path, windowName = "puzzle-capture-page") => (e) => {
        e.preventDefault();
        openCapturePopup(path, windowName, lang);
    };

    const slidingGalleryPath = advertisementScreenshotGalleryPath("sliding");
    const slidingGoogleAdPath = advertisementGoogleAdPath("sliding");
    const slidingFacebookAdPath = advertisementFacebookAdPath("sliding");
    const jigsawGalleryPath = advertisementScreenshotGalleryPath("jigsaw");
    const jigsawGoogleAdPath = advertisementGoogleAdPath("jigsaw");
    const jigsawFacebookAdPath = advertisementFacebookAdPath("jigsaw");
    const jigsawAmazonTabletPath = advertisementAmazonTabletPath("jigsaw");
    const wordWheelGalleryPath = advertisementScreenshotGalleryPath("word-wheel");
    const wordWheelGoogleAdPath = advertisementGoogleAdPath("word-wheel");
    const wordWheelFacebookAdPath = advertisementFacebookAdPath("word-wheel");
    const webGalleryPath = advertisementScreenshotGalleryPath("web");

    const slidingScenes = SLIDING_SCREENSHOT_SCENES.filter(isScreenshotSceneConfigured);
    const wordWheelScenes = WORD_WHEEL_SCREENSHOT_SCENES.filter(isWordWheelScreenshotSceneConfigured);

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", lineHeight: 1.5 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    App &amp; Screenshot Links
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    App Store destinations and draft capture pages for OBS / screenshots.
                </Typography>
                <ScreenshotLangPicker sx={{ mb: 0 }} />
            </Box>

            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, nextTab) => setActiveTab(nextTab)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    aria-label="App and screenshot link sections"
                    sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        "& .MuiTab-root": {
                            minHeight: 52,
                            fontWeight: 600,
                            textTransform: "none",
                        },
                    }}
                >
                    {[
                        "Company",
                        "Public links",
                        SLIDING_PUZZLE_QUEST_TITLE,
                        JIGSAW_MOBILE_TITLE,
                        WORD_WHEEL_QUEST_TITLE,
                        WORD_SEARCH_MINI_TITLE,
                        "WEB",
                    ].map((label, index) => (
                        <Tab
                            key={label}
                            label={label}
                            id={`app-links-tab-${index}`}
                            aria-controls={`app-links-tabpanel-${index}`}
                        />
                    ))}
                </Tabs>

                <TabPanel value={activeTab} index={0}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Studio mark for App Store / Play Store — 512×512 SVG (light silver canvas,
                        multi-genre puzzle grid).
                    </Typography>
                    <Stack spacing={0.5}>
                        <LinkRow
                            href={localizedHref(COMPANY_LOGO_PATH)}
                            onClick={openScreenshotLink(
                                COMPANY_LOGO_PATH,
                                "puzzle-company-logo",
                            )}
                            caption={`${COMPANY_LOGO_PATH} · 512×512 · download SVG / PNG`}
                        >
                            Company logo — (512×512)
                        </LinkRow>
                        <LinkRow
                            href={localizedHref(COMPANY_GOOGLE_HEADER_PATH)}
                            onClick={openScreenshotLink(
                                COMPANY_GOOGLE_HEADER_PATH,
                                "puzzle-company-google-header",
                            )}
                            caption={`${COMPANY_GOOGLE_HEADER_PATH} · 4096×2304 · JPEG ≤1 MB · under construction`}
                        >
                            Company — Google Header Image (under construction)
                        </LinkRow>
                    </Stack>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}
                    >
                        Google Play developer header
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="div">
                        Shown at the top of your developer page
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            <li>JPEG or 24-bit PNG (not transparent)</li>
                            <li>4096 px by 2304 px</li>
                            <li>Up to 1 MB</li>
                        </Box>
                    </Typography>

                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600 }}
                    >
                        Google AdMob
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                        Image assets for AdMob uploads (file size limit 5120 KB each):
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            <li>
                                <strong>Landscape</strong> — ratio 1.91:1, greater than 600×314
                                (export 1200×628)
                            </li>
                            <li>
                                <strong>Square</strong> — ratio 1:1, at least 300×300 (export
                                1200×1200)
                            </li>
                            <li>
                                <strong>Banner</strong> — 320×50 (standard AdMob banner; see app
                                tabs)
                            </li>
                        </Box>
                    </Typography>
                    <Stack spacing={0.5}>
                        <LinkRow
                            href={localizedHref(COMPANY_GOOGLE_ADMOB_PATH)}
                            onClick={openScreenshotLink(
                                COMPANY_GOOGLE_ADMOB_PATH,
                                "puzzle-company-google-admob",
                            )}
                            caption={`${COMPANY_GOOGLE_ADMOB_PATH} · landscape + square · download PNG`}
                        >
                            Company — Google AdMob (landscape & square)
                        </LinkRow>
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600 }}
                    >
                        Facebook
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                        Page cover for Puzzle Interact:
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            <li>
                                <strong>Cover</strong> — 1640×624 (displays ~820×312)
                            </li>
                            <li>
                                Promo text + image on the left; three published apps (Sliding,
                                Jigsaw, Word Wheel) bottom-right
                            </li>
                        </Box>
                    </Typography>
                    <Stack spacing={0.5}>
                        <LinkRow
                            href={localizedHref(COMPANY_FACEBOOK_COVER_PATH)}
                            onClick={openScreenshotLink(
                                COMPANY_FACEBOOK_COVER_PATH,
                                "puzzle-company-facebook-cover",
                            )}
                            caption={`${COMPANY_FACEBOOK_COVER_PATH} · 1640×624 · download PNG`}
                        >
                            Company — Facebook cover image
                        </LinkRow>
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600 }}
                    >
                        YouTube
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                        Channel cover / art for Puzzle Interact — apps trailer:
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            <li>
                                <strong>Cover</strong> — 2560×1440 (safe area ~1546×423 centered)
                            </li>
                            <li>
                                Brand text on the left; three phone mockups (Sliding, Jigsaw, Word
                                Wheel) on the right so the channel can see the apps trailer on every
                                crop
                            </li>
                        </Box>
                    </Typography>
                    <Stack spacing={0.5}>
                        <LinkRow
                            href={localizedHref(COMPANY_YOUTUBE_COVER_PATH)}
                            onClick={openScreenshotLink(
                                COMPANY_YOUTUBE_COVER_PATH,
                                "puzzle-company-youtube-cover",
                            )}
                            caption={`${COMPANY_YOUTUBE_COVER_PATH} · 2560×1440 · download PNG`}
                        >
                            Company — YouTube cover image
                        </LinkRow>
                    </Stack>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Per-product URLs for App Store Connect, Amazon, Galaxy Store, and the public
                        website (web, support, marketing, privacy, terms, data deletion). Path identifies
                        the product — e.g. Word Wheel Quest support is <code>/support/word_wheel_quest</code>.
                        Galaxy Store Seller Home / Help can use Marketing / Support; paste Data deletion
                        (Galaxy Store) for the listing <code>?platform=samsung</code> tag.
                    </Typography>
                    <Tabs
                        value={publicLinksTab}
                        onChange={(_, nextTab) => setPublicLinksTab(nextTab)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        aria-label="Public link apps"
                        sx={{
                            mb: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                            "& .MuiTab-root": {
                                minHeight: 44,
                                fontWeight: 600,
                                textTransform: "none",
                            },
                        }}
                    >
                        {PUBLIC_LINK_PRODUCTS.map(({ appId, label }) => (
                            <Tab key={appId} label={label} />
                        ))}
                    </Tabs>
                    {PUBLIC_LINK_PRODUCTS.map(({ appId, note }, index) => (
                        publicLinksTab === index ? (
                            <Box key={appId}>
                                {note ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                        {note}
                                    </Typography>
                                ) : null}
                                <PublicProductLinks appId={appId} />
                            </Box>
                        ) : null
                    ))}
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Draft pages for App Store captures.
                    </Typography>
                    <ExpiryNote />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5 }}>
                        Animation
                    </Typography>
                    <Stack spacing={0.5}>
                        {SLIDING_ANIMATION_SCENES.map((scene) => (
                            <LinkRow
                                key={scene.slug}
                                href={localizedHref(slidingAnimationPath(scene.slug))}
                                onClick={openScreenshotLink(
                                    slidingAnimationPath(scene.slug),
                                    `puzzle-sliding-animation-${scene.slug}`,
                                )}
                                caption={appendScreenshotLang(slidingAnimationPath(scene.slug), lang)}
                            >
                                Animation {scene.slug} — {scene.label}
                            </LinkRow>
                        ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5 }}>
                        Screenshots
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(slidingGalleryPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(slidingGalleryPath, "puzzle-sliding-all-screenshots", lang);
                            }}
                            caption={appendScreenshotLang(slidingGalleryPath, lang)}
                        >
                            All screenshots — {SLIDING_PUZZLE_QUEST_TITLE}
                        </LinkRow>
                        {slidingScenes.map((scene) => (
                            <LinkRow
                                key={scene.slug}
                                href={localizedHref(slidingScreenshotPath(scene.slug))}
                                onClick={openScreenshotLink(
                                    slidingScreenshotPath(scene.slug),
                                    `puzzle-sliding-screenshot-${scene.slug}`,
                                )}
                            >
                                Screenshot {scene.slug} — {scene.label}
                            </LinkRow>
                        ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Google Ads
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(slidingGoogleAdPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    slidingGoogleAdPath,
                                    "puzzle-sliding-google-ad",
                                    lang,
                                );
                            }}
                            caption={appendScreenshotLang(slidingGoogleAdPath, lang)}
                        >
                            Google Ads — Landscape / Square / Portrait
                        </LinkRow>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Facebook Ads
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(slidingFacebookAdPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    slidingFacebookAdPath,
                                    "puzzle-sliding-facebook-ad",
                                    lang,
                                );
                            }}
                            caption={appendScreenshotLang(slidingFacebookAdPath, lang)}
                        >
                            Facebook Ads — Square (1:1) / 4:5 / Vertical (9:16)
                        </LinkRow>
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600 }}
                    >
                        Google AdMob
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                        Image assets for AdMob uploads (file size limit 5120 KB each):
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            <li>
                                <strong>Landscape</strong> — 1200×628 (1.91:1)
                            </li>
                            <li>
                                <strong>Square</strong> — 1200×1200 (1:1)
                            </li>
                            <li>
                                <strong>Banner</strong> — 320×50 (standard banner)
                            </li>
                        </Box>
                    </Typography>
                    <Stack spacing={0.5}>
                        <LinkRow
                            href={localizedHref(SLIDING_GOOGLE_ADMOB_PATH)}
                            onClick={openScreenshotLink(
                                SLIDING_GOOGLE_ADMOB_PATH,
                                "puzzle-sliding-google-admob",
                            )}
                            caption={`${SLIDING_GOOGLE_ADMOB_PATH} · landscape + square + animated 320×50 GIF`}
                        >
                            {SLIDING_PUZZLE_QUEST_TITLE} — Google AdMob (landscape, square & banner)
                        </LinkRow>
                    </Stack>
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Draft pages for App Store captures.
                    </Typography>
                    <ExpiryNote />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5 }}>
                        Screenshots
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(jigsawGalleryPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(jigsawGalleryPath, "puzzle-jigsaw-all-screenshots", lang);
                            }}
                            caption={appendScreenshotLang(jigsawGalleryPath, lang)}
                        >
                            All screenshots — {JIGSAW_MOBILE_TITLE}
                        </LinkRow>
                        {JIGSAW_SCREENSHOT_SCENES.map((scene) => (
                            <LinkRow
                                key={scene.slug}
                                href={localizedHref(jigsawScreenshotPath(scene.slug))}
                                onClick={openScreenshotLink(
                                    jigsawScreenshotPath(scene.slug),
                                    `puzzle-jigsaw-screenshot-${scene.slug}`,
                                )}
                            >
                                Screenshot {scene.slug} — {scene.label}
                            </LinkRow>
                        ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Google Ads
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(jigsawGoogleAdPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    jigsawGoogleAdPath,
                                    "puzzle-jigsaw-google-ad",
                                    lang,
                                );
                            }}
                            caption={appendScreenshotLang(jigsawGoogleAdPath, lang)}
                        >
                            Google Ads — Landscape / Square / Portrait
                        </LinkRow>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Facebook Ads
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(jigsawFacebookAdPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    jigsawFacebookAdPath,
                                    "puzzle-jigsaw-facebook-ad",
                                    lang,
                                );
                            }}
                            caption={appendScreenshotLang(jigsawFacebookAdPath, lang)}
                        >
                            Facebook Ads — Square (1:1) / 4:5 / Vertical (9:16)
                        </LinkRow>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Amazon Appstore
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(jigsawAmazonTabletPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    jigsawAmazonTabletPath,
                                    "puzzle-jigsaw-amazon-tablet",
                                    lang,
                                );
                            }}
                            caption={appendScreenshotLang(jigsawAmazonTabletPath, lang)}
                        >
                            Amazon Appstore — Tablet screenshots (1024×600)
                        </LinkRow>
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600 }}
                    >
                        Google AdMob
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                        Image assets for AdMob uploads (file size limit 5120 KB each):
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            <li>
                                <strong>Landscape</strong> — 1200×628 (1.91:1)
                            </li>
                            <li>
                                <strong>Square</strong> — 1200×1200 (1:1)
                            </li>
                            <li>
                                <strong>Banner</strong> — 320×50 (standard banner)
                            </li>
                        </Box>
                    </Typography>
                    <Stack spacing={0.5}>
                        <LinkRow
                            href={localizedHref(JIGSAW_GOOGLE_ADMOB_PATH)}
                            onClick={openScreenshotLink(
                                JIGSAW_GOOGLE_ADMOB_PATH,
                                "puzzle-jigsaw-google-admob",
                            )}
                            caption={`${JIGSAW_GOOGLE_ADMOB_PATH} · landscape + square + 320×50 · download PNG`}
                        >
                            {JIGSAW_MOBILE_TITLE} — Google AdMob (landscape, square & banner)
                        </LinkRow>
                    </Stack>
                </TabPanel>

                <TabPanel value={activeTab} index={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Draft pages for App Store captures.
                    </Typography>
                    <ExpiryNote formatLabel={formatWordWheelScreenshotAccessValidThroughLabel} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5 }}>
                        Screenshots
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(wordWheelGalleryPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(wordWheelGalleryPath, "puzzle-word-wheel-all-screenshots", lang);
                            }}
                            caption={appendScreenshotLang(wordWheelGalleryPath, lang)}
                        >
                            All screenshots — {WORD_WHEEL_QUEST_TITLE}
                        </LinkRow>
                        {wordWheelScenes.map((scene) => (
                            <LinkRow
                                key={scene.slug}
                                href={localizedHref(wordWheelScreenshotPath(scene.slug))}
                                onClick={openScreenshotLink(
                                    wordWheelScreenshotPath(scene.slug),
                                    `puzzle-word-wheel-screenshot-${scene.slug}`,
                                )}
                                caption={appendScreenshotLang(wordWheelScreenshotPath(scene.slug), lang)}
                            >
                                Screenshot {scene.slug} — {scene.label}
                            </LinkRow>
                        ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Google Ads
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(wordWheelGoogleAdPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    wordWheelGoogleAdPath,
                                    "puzzle-word-wheel-google-ad",
                                    lang,
                                );
                            }}
                            caption={appendScreenshotLang(wordWheelGoogleAdPath, lang)}
                        >
                            Google Ads — Landscape / Square / Portrait
                        </LinkRow>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Facebook Ads
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(wordWheelFacebookAdPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    wordWheelFacebookAdPath,
                                    "puzzle-word-wheel-facebook-ad",
                                    lang,
                                );
                            }}
                            caption={appendScreenshotLang(wordWheelFacebookAdPath, lang)}
                        >
                            Facebook Ads — Square (1:1) / 4:5 / Vertical (9:16)
                        </LinkRow>
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600 }}
                    >
                        Google AdMob
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                        Image assets for AdMob uploads (file size limit 5120 KB each):
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            <li>
                                <strong>Landscape</strong> — 1200×628 (1.91:1)
                            </li>
                            <li>
                                <strong>Square</strong> — 1200×1200 (1:1)
                            </li>
                            <li>
                                <strong>Banner</strong> — 320×50 (standard banner)
                            </li>
                        </Box>
                    </Typography>
                    <Stack spacing={0.5}>
                        <LinkRow
                            href={localizedHref(WORD_WHEEL_GOOGLE_ADMOB_PATH)}
                            onClick={openScreenshotLink(
                                WORD_WHEEL_GOOGLE_ADMOB_PATH,
                                "puzzle-word-wheel-google-admob",
                            )}
                            caption={`${WORD_WHEEL_GOOGLE_ADMOB_PATH} · landscape + square + 320×50 · download PNG`}
                        >
                            {WORD_WHEEL_QUEST_TITLE} — Google AdMob (landscape, square & banner)
                        </LinkRow>
                    </Stack>
                </TabPanel>

                <TabPanel value={activeTab} index={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Draft pages for App Store captures.
                    </Typography>
                    <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 600, mb: 1.5 }}>
                        Placeholder links copied from {WORD_WHEEL_QUEST_TITLE} — will be updated for{" "}
                        {WORD_SEARCH_MINI_TITLE}.
                    </Typography>
                    <ExpiryNote formatLabel={formatWordWheelScreenshotAccessValidThroughLabel} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5 }}>
                        Screenshots
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(wordWheelGalleryPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(wordWheelGalleryPath, "puzzle-word-search-mini-all-screenshots", lang);
                            }}
                            caption={`${appendScreenshotLang(wordWheelGalleryPath, lang)} · placeholder`}
                        >
                            All screenshots — {WORD_SEARCH_MINI_TITLE}
                        </LinkRow>
                        {wordWheelScenes.map((scene) => (
                            <LinkRow
                                key={`wsm-${scene.slug}`}
                                href={localizedHref(wordWheelScreenshotPath(scene.slug))}
                                onClick={openScreenshotLink(
                                    wordWheelScreenshotPath(scene.slug),
                                    `puzzle-word-search-mini-screenshot-${scene.slug}`,
                                )}
                                caption={`${appendScreenshotLang(wordWheelScreenshotPath(scene.slug), lang)} · placeholder`}
                            >
                                Screenshot {scene.slug} — {scene.label}
                            </LinkRow>
                        ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Google Ads
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(wordWheelGoogleAdPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    wordWheelGoogleAdPath,
                                    "puzzle-word-search-mini-google-ad",
                                    lang,
                                );
                            }}
                            caption={`${appendScreenshotLang(wordWheelGoogleAdPath, lang)} · placeholder`}
                        >
                            Google Ads — Landscape / Square / Portrait
                        </LinkRow>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Facebook Ads
                    </Typography>
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(wordWheelFacebookAdPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(
                                    wordWheelFacebookAdPath,
                                    "puzzle-word-search-mini-facebook-ad",
                                    lang,
                                );
                            }}
                            caption={`${appendScreenshotLang(wordWheelFacebookAdPath, lang)} · placeholder`}
                        >
                            Facebook Ads — Square (1:1) / 4:5 / Vertical (9:16)
                        </LinkRow>
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontWeight: 600 }}
                    >
                        Google AdMob
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                        Image assets for AdMob uploads (file size limit 5120 KB each):
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            <li>
                                <strong>Landscape</strong> — 1200×628 (1.91:1)
                            </li>
                            <li>
                                <strong>Square</strong> — 1200×1200 (1:1)
                            </li>
                            <li>
                                <strong>Banner</strong> — 320×50 (standard banner)
                            </li>
                        </Box>
                    </Typography>
                    <Stack spacing={0.5}>
                        <LinkRow
                            href={localizedHref(WORD_WHEEL_GOOGLE_ADMOB_PATH)}
                            onClick={openScreenshotLink(
                                WORD_WHEEL_GOOGLE_ADMOB_PATH,
                                "puzzle-word-search-mini-google-admob",
                            )}
                            caption={`${WORD_WHEEL_GOOGLE_ADMOB_PATH} · placeholder · landscape + square + 320×50`}
                        >
                            {WORD_SEARCH_MINI_TITLE} — Google AdMob (landscape, square & banner)
                        </LinkRow>
                    </Stack>
                </TabPanel>

                <TabPanel value={activeTab} index={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Web Google Ads creatives (English). Sample art — not live templates. Max file
                        size 5 MB each.
                    </Typography>
                    <Stack spacing={0.25} sx={{ mb: 2 }}>
                        <LinkRow
                            href={localizedHref(webGalleryPath)}
                            onClick={(e) => {
                                e.preventDefault();
                                openGalleryPopup(webGalleryPath, "puzzle-web-all-screenshots", lang);
                            }}
                            caption={appendScreenshotLang(webGalleryPath, lang)}
                        >
                            All screenshots — Web Google Ads
                        </LinkRow>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Google Ads shots
                    </Typography>
                    {webAdShotsByGroup().map(({ group, shots }) => (
                        <Box key={group} sx={{ mb: 1.5 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.25, fontWeight: 700 }}
                            >
                                {group}
                            </Typography>
                            <Stack spacing={0.25}>
                                {shots.map((shot) => {
                                    const frame = WEB_AD_FRAME[shot.asset];
                                    const path = webGoogleAdPath(shot.asset, shot.id);
                                    return (
                                        <LinkRow
                                            key={`${shot.asset}-${shot.id}`}
                                            href={localizedHref(path)}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                openBrowserPopup(
                                                    screenshotPageHref(path, lang),
                                                    `puzzle-web-google-ad-${shot.asset}-${shot.id}`,
                                                    Math.min(frame.width + 80, 1280),
                                                    Math.min(frame.height + 220, 1100),
                                                );
                                            }}
                                            caption={`${frame.ratio} · ${frame.width} × ${frame.height} · 5 MB`}
                                        >
                                            {shot.label}
                                        </LinkRow>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ))}
                    <Stack spacing={0.25}>
                        <LinkRow
                            href={localizedHref(COMPANY_LOGO_PATH)}
                            onClick={openScreenshotLink(
                                COMPANY_LOGO_PATH,
                                "puzzle-company-logo",
                            )}
                            caption={`${COMPANY_LOGO_PATH} · 512×512 · download SVG / PNG`}
                        >
                            Logo (512×512)
                        </LinkRow>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5 }}>
                        Play pages (OBS)
                    </Typography>
                    <Stack spacing={0.25}>
                        {WEB_PLAY_PAGES.map((shot) => (
                            <LinkRow
                                key={shot.path}
                                href={localizedHref(shot.path)}
                                onClick={(event) => {
                                    event.preventDefault();
                                    openBrowserPopup(
                                        screenshotPageHref(shot.path, lang),
                                        `puzzle-web-shot-${shot.label}`,
                                        1280,
                                        800,
                                    );
                                }}
                                caption={shot.path}
                            >
                                {shot.label}
                            </LinkRow>
                        ))}
                    </Stack>
                </TabPanel>
            </Box>
        </Box>
    );
}
